import React, { useEffect, useRef, useState } from 'react';
import 'react-phone-number-input/style.css';
import PhoneInput, { isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import FieldLabel from '@v7/form/src/components/Tools/FieldLabel';
import TriggerValidation from '@v7/form/src/components/Tools/TriggerValidation';
import { PHONE_NUMBER_ERROR_MESSAGES, VALIDATION_ERROR_MESSAGES } from '@v7/form/src/utils/constant';

interface PhoneNumberProps {
  id: string;
  value?: string;
  onChange?: (value: string) => void;
  defaultCountry?: string;
  className?: string;
  placeholder?: string;
  required?: boolean;
  label?: string;
  hiddenLabel?: boolean;
  disabled?: boolean;
  labelSize?: string;
  tooltip?: string;
  name?: string;
}

interface Error {
  triggerInvalid: boolean;
  message: string;
}

export const PhoneNumber = (props: PhoneNumberProps) => {
  const {
    value = '',
    onChange,
    placeholder,
    id,
    required,
    label,
    hiddenLabel,
    disabled,
    labelSize,
    tooltip,
    name,
  } = props;

  const [phoneNumber, setPhoneNumber] = useState<string>(value);
  const [error, setError] = useState<Error | null>({
    triggerInvalid: false,
    message: '',
  });
  const inputRef = useRef<any>(null);

  useEffect(() => {
    if (value !== phoneNumber) {
      setPhoneNumber(value);
    }
  }, [value]);

  useEffect(() => {
    if (required && !phoneNumber) {
      inputRef.current?.setCustomValidity(VALIDATION_ERROR_MESSAGES.REQUIRED);
    } else if (phoneNumber && (!isValidPhoneNumber(phoneNumber) || !parsePhoneNumber(phoneNumber))) {
      inputRef.current?.setCustomValidity(PHONE_NUMBER_ERROR_MESSAGES.INVALID);
    } else {
      inputRef.current?.setCustomValidity('');
    }

    setError((prev) => ({
      ...prev,
      triggerInvalid: !!inputRef.current?.validationMessage,
      message: inputRef.current?.validationMessage,
    }));
  }, [phoneNumber, required]);

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value || '');
    if (onChange) {
      onChange(value || '');
    }
  };

  return (
    <div data-testid="phonenumber-field" className="w-full max-w-[25rem]" key={id}>
      {label && (
        <FieldLabel
          value={label}
          required={required}
          hiddenLabel={hiddenLabel}
          disabled={disabled}
          size={labelSize}
          id={id}
          tooltip={tooltip}
        >
          {label}
        </FieldLabel>
      )}
      <div className="p-2 border border-gray-300 rounded-md">
        <PhoneInput
          ref={inputRef}
          required={required}
          international
          value={phoneNumber}
          placeholder={placeholder}
          onChange={handlePhoneNumberChange}
          defaultCountry="IN"
          countryCallingCodeEditable={false}
        />
      </div>
      <TriggerValidation
        validationTrigger={error?.triggerInvalid}
        validationMessage={error?.message}
        name={name}
        alterClassName="mt-2"
      />
    </div>
  );
};

export default PhoneNumber;
